import { Stack, Text } from "@chakra-ui/react";
import * as React from "react";

import {
  KeywordAgentTerm,
  excludeKeywordAgentTerm,
  restoreKeywordAgentTerm,
} from "../api/keyword-agent";
import { useLoading } from "../hooks/loading";
import { useToaster } from "../hooks/toaster";
import { brand } from "../lib/brand";
import { Button } from "./button";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { NoteField } from "./note-field";
import { toaster } from "./toaster";

export type SearchTermNoteMode = "restore" | "exclude";

interface SearchTermNoteDialogProps {
  term: KeywordAgentTerm | null;
  mode: SearchTermNoteMode;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  workspaceSlug: string;
  customerId: string;
  loginCustomerId: string | null;
  campaignId: number;
  adGroupId: number;
}

// Both take a note. Either way the user is overruling a call that stands
// -- the agent's or their own earlier one -- and the note is what the next
// judging run has to go on.
const modes = {
  restore: {
    submit: restoreKeywordAgentTerm,
    title: (term: string) => `Put "${term}" back?`,
    description: `The exclusion comes off and this term can match your ads again. Tell ${brand.productName} why it got this wrong.`,
    label: `Teach ${brand.productName}`,
    placeholder:
      "e.g. this wording is how our best customers describe the problem",
    hint: `Required. ${brand.productName} won't exclude this term again.`,
    button: "Remove exclusion",
    done: (term: string) => `"${term}" is back in the auction`,
  },
  exclude: {
    submit: excludeKeywordAgentTerm,
    title: (term: string) => `Exclude "${term}" again?`,
    description: "This term stops matching your ads immediately.",
    label: `Tell ${brand.productName} why`,
    placeholder: "e.g. we stopped selling these last year",
    hint: `Required, this is how ${brand.productName} learns.`,
    button: "Exclude again",
    done: (term: string) => `"${term}" is excluded again`,
  },
};

export function SearchTermNoteDialog({
  adGroupId,
  campaignId,
  customerId,
  loginCustomerId,
  mode,
  onOpenChange,
  onSuccess,
  term,
  workspaceSlug,
}: SearchTermNoteDialogProps) {
  const [note, setNote] = React.useState("");
  const [isWorking, withWorking] = useLoading();
  const withToaster = useToaster();
  const ruling = modes[mode];
  const answer = note.trim();

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) {
      setNote("");
    }
    onOpenChange(details);
  };

  const confirm = async () => {
    if (!term) return;
    await withWorking(async () => {
      await withToaster(async () => {
        await ruling.submit(
          workspaceSlug,
          customerId,
          loginCustomerId,
          campaignId,
          adGroupId,
          term.id,
          answer,
        );
        toaster.create({ type: "success", title: ruling.done(term.term) });
        handleOpenChange({ open: false });
        onSuccess();
      });
    });
  };

  return (
    <DialogRoot open={!!term} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <Stack gap="1">
            <DialogTitle>{ruling.title(term?.term ?? "")}</DialogTitle>
            <DialogDescription>{ruling.description}</DialogDescription>
          </Stack>
        </DialogHeader>
        <DialogBody>
          <Stack gap="4">
            {/* What they said last time, which this ruling overturns and
                the new note replaces. */}
            {mode === "exclude" && term?.["user-note"] && (
              <Stack gap="1">
                <Text color="fg.muted" fontSize="sm">
                  When you restored it, you told {brand.productName}:
                </Text>
                <Text fontSize="sm">&ldquo;{term["user-note"]}&rdquo;</Text>
              </Stack>
            )}
            <NoteField
              label={ruling.label}
              placeholder={ruling.placeholder}
              hint={ruling.hint}
              value={note}
              onChange={setNote}
            />
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            disabled={isWorking}
            onClick={() => handleOpenChange({ open: false })}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            loading={isWorking}
            disabled={!answer}
            onClick={confirm}
          >
            {ruling.button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
